import { Component, DoCheck, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastrService } from 'ngx-toastr';

import { SiteCardComponent, SiteBulkAction, SiteBulkSets } from '../site-card/site-card.component';
import { ProjectVm, SiteVm } from '../list-grouping.util';
import { SignedAtpSiteVisit } from '../../model/signed-atp-site-visit';
import { CommonUtilityServiceService } from '../../services/common-utility-service.service';
import { SiteService } from '../../services/site.service';
import { ProjectService } from '../../services/project.service';
import { ModalService } from '../../services/modal.service';
import { getStoredUser } from '../../helpers/user-storage.util';
import {
  BulkEligibility,
  SiteDetail,
  acceptAllOffers,
  buildBulkProjectDetail,
  declineAll,
  signAtpAllAwarded,
  spansOtherSites,
  submitAllQuotes,
  BULK_SUBMIT_DISABLED_REASON,
  BULK_ACCEPT_DISABLED_REASON,
  BULK_ATP_DISABLED_REASON,
  BULK_DECLINE_DISABLED_REASON,
  ATP_IN_PROGRESS_REASON,
  siteScopeLabel,
} from '../bulk-eligibility.util';
import { ProjectActionLockService, PRODUCTS_LOCKED_REASON } from '../../services/project-action-lock.service';
import { BatchDeclineService } from '../../services/batch-decline.service';
import { BatchDeclineRequest } from '../../services/proposal.service';
import { BatchDeclineItem } from '../../model/batch-decline';
import { DECLINE_ALL_TOO_MANY_REASON, batchDeclineItem, tooManyToDecline } from '../batch-decline.util';
import { DeclineDialogComponent } from '../../decline-dialog/decline-dialog.component';

// Project- and site-scoped bulk ACTION dialogs, sharing the existing write flows
// (same shape `product-line` reuses the per-product ones). The post-write
// CONFIRMATION dialogs stay page-hosted via ModalService: a bulk write pushes
// a project-detail refresh that re-renders the list and destroys this banner,
// so a confirmation hosted here would never render — exactly the reason
// `product-line` routes its own confirmations through the page.
import { AllbidsDialogComponent } from '../../allbids-dialog/allbids-dialog.component';
import { AcceptOfferedProposalDialogComponent } from '../../accept-offered-proposal-dialog/accept-offered-proposal-dialog.component';
import { SignAtpDialogComponent } from '../../sign-atp-dialog/sign-atp-dialog.component';

// CONNECT 2.0 list screens (Spec 4) — the top-level nesting unit: a project
// banner shared once, above the list of its site cards. Replaces the 1.0
// `info-group` (`info-header` + `info-table`) wrapper.
//
// Task 3 banner details. The raw project/site/proposal shape has no
// dedicated project-level "category"/"service scope"/"requested turnaround"
// fields (confirmed against `info-header`, `info-table-item`, and every
// dialog that reads `data.*` — see list-grouping.util.ts's header comment for
// the full flat shape). Every getter below is instead derived from the real
// per-product fields already surfaced on ProductVm/SiteVm, and is documented
// where it degrades rather than fabricating a number the API doesn't expose.
//
// CON-154 additionally makes this the single host of the project-level bulk
// actions (Submit All Quotes / Accept All Offers / Sign ATP for All Awarded
// Sites), which is why an otherwise presentational banner now owns writes.
// Hosting them here means every list screen gets them from one implementation
// AND that the per-screen gating falls out for free: `project.raw` is the
// already page-filtered project (each page clones the parent with a
// status-filtered `sites` before `toProjectVms`), so a screen whose products
// are never in an action's phase renders no button for it — no `type` checks
// and no per-page conditionals to keep in sync.
@Component({
  standalone: true,
  selector: 'app-project-group',
  imports: [
    CommonModule,
    MatTooltipModule,
    SiteCardComponent,
    AllbidsDialogComponent,
    AcceptOfferedProposalDialogComponent,
    SignAtpDialogComponent,
    DeclineDialogComponent,
  ],
  templateUrl: './project-group.component.html',
  styleUrl: './project-group.component.scss',
})
export class ProjectGroupComponent implements DoCheck {
  readonly submitDisabledReason = BULK_SUBMIT_DISABLED_REASON;
  readonly acceptDisabledReason = BULK_ACCEPT_DISABLED_REASON;
  readonly atpDisabledReason = BULK_ATP_DISABLED_REASON;
  readonly declineDisabledReason = BULK_DECLINE_DISABLED_REASON;
  readonly declineTooManyReason = DECLINE_ALL_TOO_MANY_REASON;
  readonly lockedReason = PRODUCTS_LOCKED_REASON;

  // Whether a write is still running on any of these products. Each bulk
  // control and handler checks the rows it acts on, so a write on another
  // product of this project leaves it enabled.
  rowsLocked(rows: readonly SiteDetail[]): boolean {
    return rows.some((row) => this.lock.isLocked(row.proposal?.id));
  }

  /**
   * Why a banner button is disabled, or null when it is enabled. The order is
   * the rule: a running write on one of its products, then (Decline All only)
   * the backend's batch limit, then no product ready for the action.
   */
  bulkDisabledReason(rows: readonly SiteDetail[], dataReason: string, batchLimited = false): string | null {
    if (this.rowsLocked(rows)) {
      return this.lockedReason;
    }
    if (batchLimited && tooManyToDecline(rows)) {
      return this.declineTooManyReason;
    }
    if (!rows.length) {
      return dataReason;
    }
    return null;
  }

  // The project view-model (see list-grouping.util.ts). Required.
  @Input({ required: true }) project!: ProjectVm;

  // Screen context: 'invitations' | 'proposals' | 'active'. Forwarded down so
  // site cards and product lines render the correct variant.
  @Input() type: string = '';

  // Task 3 (Project History): read-only mode. Forwarded straight down to
  // every site card (and from there to every product line), which is where
  // the actual action-area guarding happens — see SiteCardComponent /
  // ProductLineComponent. Defaults false so every existing consumer
  // (Invitations/Pending/Active + Project Overview) is unaffected.
  @Input() readOnly: boolean = false;

  // Optional project-scope segment for the site/product testids, forwarded
  // straight through to every site card. The multi-project LIST pages
  // (invitations/proposals/active/project-history) set this to the project's
  // stable key so per-project site cards don't collide on a shared `siteNumber`
  // across sibling project groups; left empty on the single-project
  // project-overview surface, where the bare `site-<n>-...` ids are preserved.
  @Input() testIdProjectScope: string = '';

  // Last leg of the product-line → site-card → project-group → page relay
  // carrying the site visits an ATP signature left schedulable. The page binds
  // this onto `atp-sign-accepted-dialog`'s `siteVisits` input; nothing in this
  // subtree reads it.
  @Output() atpSigned = new EventEmitter<SignedAtpSiteVisit[]>();

  private user = getStoredUser();

  constructor(
    private utilityService: CommonUtilityServiceService,
    private siteService: SiteService,
    private projectService: ProjectService,
    private modalService: ModalService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    private lock: ProjectActionLockService,
    private batchDeclineService: BatchDeclineService
  ) {}

  // trackBy for the site *ngFor — keep each site card's DOM (and its expand/
  // collapse state) across the list refreshes the action writes push, instead
  // of tearing every card down. Keyed on the stable grouping key.
  trackBySiteKey(index: number, site: SiteVm): string {
    return site.key;
  }

  // ---------------------------------------------------------------------
  // Task 3: banner details
  // ---------------------------------------------------------------------

  /** Physical site count — real, direct from the grouped view-model. */
  get siteCount(): number {
    return this.project.sites.length;
  }

  /** Total product (service line-item) count across every site — used for
   *  the Active header's degraded "status only" line (see the template)
   *  since no per-product "complete" status exists in the domain (the known
   *  statuses run INVITATION…REVISION_PENDING/ACTIVE with no
   *  terminal "done" state — see ProductLineComponent's PILL_MAP and
   *  CommonUtilityServiceService.determineDocumentTypeByProposalStatus). */
  get productCount(): number {
    return this.project.sites.reduce((sum, site) => sum + site.products.length, 0);
  }

  /** Distinct service types (`site.siteType`, already a display-ready string
   *  like "Phase I ESA" — see accept-offered-proposal-dialog usage) across
   *  the project's products, in first-seen order. Backs both the category
   *  pill and the `Service:` scope line — there's no separate project-level
   *  category field to read instead. */
  get serviceTypes(): string[] {
    const seen = new Set<string>();
    const types: string[] = [];
    for (const site of this.project.sites) {
      for (const product of site.products) {
        const type = product.site?.siteType;
        if (type && !seen.has(type)) {
          seen.add(type);
          types.push(type);
        }
      }
    }
    return types;
  }

  /** Category pill text: the single service type when the project only
   *  spans one, else a compact "N Services" summary. */
  get categoryLabel(): string {
    const types = this.serviceTypes;
    if (types.length === 0) return 'Service';
    if (types.length === 1) return types[0];
    return `${types.length} Services`;
  }

  /** `Service:` scope line — the full distinct list. */
  get serviceScopeLabel(): string {
    const types = this.serviceTypes;
    return types.length ? types.join(', ') : 'Not Provided';
  }

  /** Project-level ALTA "Table A" band — ported from the legacy `info-group`
   *  (and `project-overview-container`), which both keyed off the flat
   *  `SiteDetail.site.serviceType`. Read `project.raw` rather than the
   *  grouped `sites`/`products` view-model: grouping only keeps a single
   *  representative `site` per physical site, so a non-ALTA product listed
   *  first on a site could hide an ALTA product on that same site. */
  get showTableA(): boolean {
    return !!this.project.raw?.sites?.some(
      (siteDetail: any) => siteDetail.site?.serviceType === 'NEXUS_ALTA_SURVEY'
    );
  }

  /** Formatted Table A items string, e.g. "1, 2, 3, 4, 6a, 6b ($1M E&O)" —
   *  same `CommonUtilityServiceService.formatTableAFromArray` call as 1.0;
   *  `response[1]` is the item list (with the E&O suffix folded in). */
  get tableA(): string {
    if (!this.showTableA || !this.project.raw?.tableA?.length) return '';
    const response = this.utilityService.formatTableAFromArray(this.project.raw.tableA);
    return response ? response[1] : '';
  }

  /**
   * The Item 20/21 free text, and the label naming which item it came from.
   *
   * <b>This is the FOURTH copy of the Table A banner.</b> `info-table`,
   * `project-overview-table` and the workspace callout each carry their own, and
   * this one is the LIVE list surface — nothing renders `info-group`, so the
   * banner a vendor actually sees on Invitations / Proposals / Active is this
   * one. The Item 20/21 fix reached the other three and missed this, which left
   * the text a client typed invisible on the surface most likely to be looked at.
   *
   * Index 0 is the text and index 2 names the item ("Item 20" on a survey
   * predating Feb 2026, "Item 21" after) — both were being discarded here in
   * exactly the way `info-group` was.
   */
  get tableAItemText(): string {
    return this.tableAResponse[0] ?? '';
  }

  get tableAItemLabel(): string {
    return this.tableAResponse[2] ?? '';
  }

  /** One formatter call shared by the three getters above, so a template reading
   *  all of them does not format the same array three times per change
   *  detection. */
  private get tableAResponse(): string[] {
    if (!this.showTableA || !this.project.raw?.tableA?.length) return [];
    return this.utilityService.formatTableAFromArray(this.project.raw.tableA) ?? [];
  }

  /** Distinct `site.desiredByDate` values (the client's requested-by date,
   *  per site — the only "requested turnaround" field the API actually
   *  carries; there is no project-level requested-business-days field to
   *  read, so this surfaces the real date rather than inventing a day
   *  count). */
  private get requestedDates(): Date[] {
    const seen = new Set<number>();
    const dates: Date[] = [];
    for (const site of this.project.sites) {
      for (const product of site.products) {
        const raw = product.site?.desiredByDate;
        if (!raw) continue;
        const date = new Date(raw);
        const time = date.getTime();
        if (!Number.isNaN(time) && !seen.has(time)) {
          seen.add(time);
          dates.push(date);
        }
      }
    }
    return dates;
  }

  /** True when every site that has a requested date agrees on the same one
   *  (the common case); the template shows that single date. */
  get requestedDate(): Date | null {
    const dates = this.requestedDates;
    return dates.length === 1 ? dates[0] : null;
  }

  /** True when sites disagree on their requested date — shown as "Varies by
   *  site" instead of picking one arbitrarily. */
  get requestedVariesBySite(): boolean {
    return this.requestedDates.length > 1;
  }

  /** No site carries a requested date at all — the `Requested:` line is
   *  omitted rather than showing an empty/fabricated value. */
  get hasRequestedInfo(): boolean {
    return this.requestedDates.length > 0;
  }

  get hasSpecialRequirements(): boolean {
    return !!this.project.specialQuoteRequirements?.trim();
  }

  /** Earliest signed ATP across the project's products — mirrors the
   *  earliest-date reduction the legacy `info-header` used for its own
   *  "Activation Date" stamp, but also carries the signer's name
   *  (`proposal.atpSignedBy`, round-tripped by SiteService.signAtp but never
   *  previously displayed anywhere in the app). Null when nothing is signed
   *  yet, which the Active screen shouldn't hit in practice but the template
   *  degrades cleanly for regardless. */
  get atpStamp(): { signedBy: string; signedDate: any } | null {
    let earliest: { signedBy: string; signedDate: any } | null = null;
    for (const site of this.project.sites) {
      for (const product of site.products) {
        const signedDate = product.proposal?.atpSignedDate;
        if (!signedDate) continue;
        if (!earliest || new Date(signedDate).getTime() < new Date(earliest.signedDate).getTime()) {
          earliest = { signedBy: product.proposal?.atpSignedBy ?? '', signedDate };
        }
      }
    }
    return earliest;
  }

  // ===========================================================================
  // CON-154 — project-level bulk actions.
  // ===========================================================================

  // ---------------------------------------------------------------------------
  // Eligibility. Three `{ inPhase, eligible }` pairs off `project.raw.sites`
  // (see bulk-eligibility.util.ts), memoized so they are NOT recomputed on
  // every change detection tick. The cache is keyed on TWO things, and needs
  // both:
  //
  //  - the identity of the `sites` array — cheap, and what catches a wholesale
  //    replacement (a refresh emission hands down a new project object); and
  //  - a fingerprint of the fields the three predicates actually read, per row
  //    (proposal id/status/bid/proposedTurnTime/proposedDeliverDate and the
  //    site's desiredByDate).
  //
  // The fingerprint is the load-bearing half. `ProjectService`'s
  // `patchProposalDraftInCache` mirrors a typed price / turn time into the
  // shared cache by mutating `siteDetail.proposal` IN PLACE — same `sites`
  // array, same rows, and deliberately no `.next()` (re-emitting would rebuild
  // the list and interrupt the vendor's typing). `product-line`'s
  // `onPriceChange` and `onTurnTimeChange` call it on every keystroke (`savePrice`
  // and `saveTurnTime` then do the server write on blur), so identity alone
  // would serve a pre-typing answer: a PA who priced and dated every invitation
  // would still see "Submit All Quotes" disabled, and a cleared price would
  // stay eligible.
  //
  // Memoized getters rather than an `ngOnChanges` hook on purpose, for two
  // reasons: `ngOnChanges` does not fire when a host assigns `project`
  // directly (which is how several existing specs drive this component), and
  // the template feeds these arrays straight into dialog inputs — a getter
  // returning a fresh array each call would trip NG0100
  // (ExpressionChangedAfterItHasBeenChecked) on the very next verification
  // pass. The fingerprint keeps that safe: within a single change-detection
  // pass nothing mutates, so the key is identical and the SAME cached object
  // is handed back; a genuinely changed key means a new object is the correct
  // answer.
  //
  // The key is built once per change-detection pass, not on every read: the
  // template reads these getters dozens of times a pass, and each build walks
  // every row. `ngDoCheck` runs at the start of each pass (also when a host
  // assigns `project` directly) and marks the key stale; the first read in the
  // pass rebuilds it and the rest reuse it. Nothing mutates the rows during a
  // pass (typing runs in an event handler, which then starts a new pass), so
  // reads between passes, such as a click handler's, see what the screen shows.
  // ---------------------------------------------------------------------------
  private bulkKeyChecked = false;
  private bulkCacheFor: SiteDetail[] | null | undefined = undefined;
  private bulkCacheGroupedFor: SiteVm[] | undefined;
  private bulkCacheKey: string | null = null;
  private bulkCache: {
    submit: BulkEligibility;
    accept: BulkEligibility;
    atp: BulkEligibility;
    decline: BulkEligibility;
    bySite: Map<string, SiteBulkSets>;
  } | null = null;
  private readonly noSiteBulk: SiteBulkSets = { submit: null, accept: null, atp: null, decline: null };

  /**
   * Cache key for the memo below: precisely the fields the four predicates in
   * `bulk-eligibility.util.ts` read, concatenated per row in array order.
   * Deliberately NOT a `JSON.stringify` of the rows — a site detail carries
   * parcels, documents and review state that would make the key expensive to
   * build and churn on changes that cannot affect eligibility.
   *
   * A newline after every field keeps the key unambiguous with no escaping:
   * the row width is fixed at six, and none of these values (proposal ids,
   * status enums, numbers, ISO dates) can itself contain a newline.
   */
  private bulkFingerprint(sites: SiteDetail[] | null): string {
    if (!Array.isArray(sites)) {
      return '';
    }
    let key = '';
    for (const detail of sites) {
      const proposal = detail?.proposal;
      key +=
        `${proposal?.id ?? ''}\n` +
        `${proposal?.status ?? ''}\n` +
        `${proposal?.bid ?? ''}\n` +
        `${proposal?.proposedTurnTime ?? ''}\n` +
        `${proposal?.proposedDeliverDate ?? ''}\n` +
        `${detail?.site?.desiredByDate ?? ''}\n`;
    }
    return key;
  }

  ngDoCheck(): void {
    this.bulkKeyChecked = false;
  }

  private get bulk(): {
    submit: BulkEligibility;
    accept: BulkEligibility;
    atp: BulkEligibility;
    decline: BulkEligibility;
    bySite: Map<string, SiteBulkSets>;
  } {
    const sites: SiteDetail[] | null = this.project?.raw?.sites ?? null;
    if (
      this.bulkCache !== null &&
      this.bulkKeyChecked &&
      this.bulkCacheFor === sites &&
      this.bulkCacheGroupedFor === this.project?.sites
    ) {
      return this.bulkCache;
    }
    this.bulkKeyChecked = true;
    const key = `${this.readOnly}\n${this.bulkFingerprint(sites)}`;
    if (
      this.bulkCache === null ||
      this.bulkCacheFor !== sites ||
      this.bulkCacheGroupedFor !== this.project?.sites ||
      this.bulkCacheKey !== key
    ) {
      this.bulkCacheFor = sites;
      this.bulkCacheGroupedFor = this.project?.sites;
      this.bulkCacheKey = key;
      const projectSets = {
        submit: submitAllQuotes(sites),
        accept: acceptAllOffers(sites),
        atp: signAtpAllAwarded(sites),
        decline: declineAll(sites),
      };
      const bySite = new Map<string, SiteBulkSets>();
      for (const site of this.project?.sites ?? []) {
        const rows = site.products.map((product) => product.siteDetail);
        const sets = {
          submit: submitAllQuotes(rows),
          accept: acceptAllOffers(rows),
          atp: signAtpAllAwarded(rows),
          decline: declineAll(rows),
        };
        const entry: SiteBulkSets = { ...this.noSiteBulk };
        if (!this.readOnly) {
          for (const action of ['submit', 'accept', 'atp', 'decline'] as const) {
            if (sets[action].inPhase.length > 1 && spansOtherSites(projectSets[action], sets[action])) {
              entry[action] = sets[action];
            }
          }
        }
        bySite.set(site.key, entry);
      }
      this.bulkCache = { ...projectSets, bySite };
    }
    return this.bulkCache;
  }

  /** Submit All Quotes — INVITATION products, and the priced+dated subset. */
  get bulkSubmit(): BulkEligibility {
    return this.bulk.submit;
  }

  /** Accept All Offers — OFFERED products (RE_OFFERED excluded, decision 3). */
  get bulkAccept(): BulkEligibility {
    return this.bulk.accept;
  }

  /** Sign All ATPs — BID_WON products. */
  get bulkAtp(): BulkEligibility {
    return this.bulk.atp;
  }

  get bulkDecline(): BulkEligibility {
    return this.bulk.decline;
  }

  // Resolve once outside the site's template loop: the memo key scans every row.
  get siteBulkSets(): ReadonlyMap<string, SiteBulkSets> {
    return this.bulk.bySite;
  }

  /**
   * Whether the cluster renders at all. A bulk button is ABSENT when its
   * `inPhase` set holds fewer than TWO products, and DISABLED with a tooltip
   * when it holds two or more but `eligible` is empty. `readOnly` (Project
   * History) hard-suppresses all four, joining every other write control the
   * flag already gates.
   *
   * The two-or-more threshold, and why it counts PRODUCTS rather than physical
   * sites, is documented in full on the first button's `*ngIf` in the template
   * — this getter only mirrors it so a phase holding exactly one product does
   * not leave an empty `.project-group__bulk` container behind.
   *
   * This is what makes `/active` and `/project-history` show nothing: no
   * product on either screen is ever in INVITATION, OFFERED or BID_WON, so
   * all four `inPhase` sets are empty and the banner keeps its normal height.
   */
  get showBulkActions(): boolean {
    return (
      !this.readOnly &&
      (this.bulkSubmit.inPhase.length > 1 ||
        this.bulkAccept.inPhase.length > 1 ||
        this.bulkAtp.inPhase.length > 1 ||
        this.bulkDecline.inPhase.length > 1)
    );
  }

  /** Per-button testid, project-scoped so sibling banners never collide. */
  bulkTestId(suffix: string): string {
    return `project-${this.project.projectId}-${suffix}`;
  }

  // ---------------------------------------------------------------------------
  // Dialog state. The eligible set is SNAPSHOT onto these fields when the
  // button is clicked rather than bound live off the getters, so a feed
  // emission arriving while a dialog is open cannot change the payload under
  // the vendor after they have read the count and the itemized list. Mirrors
  // `product-line`'s `writeProjectDetail`.
  // ---------------------------------------------------------------------------
  allBidsActive: boolean = false;
  bulkQuoteSites: SiteDetail[] = [];

  acceptAllOffersActive: boolean = false;
  bulkOfferSites: SiteDetail[] = [];
  acceptScopeLabel = '';

  signAtpActive: boolean = false;
  bulkAtpSites: SiteDetail[] = [];
  private atpGenerating = false;

  /** A Sign All ATPs is generating its document or has its sign dialog open. */
  get atpInProgress(): boolean {
    return this.atpGenerating || this.signAtpActive;
  }

  /**
   * The banner's Sign All ATPs reason: a running write on its products, then
   * an ATP already in progress on this project, then the shared data rule.
   */
  get atpBannerDisabledReason(): string | null {
    if (!this.rowsLocked(this.bulkAtp.eligible) && this.atpInProgress) {
      return ATP_IN_PROGRESS_REASON;
    }
    return this.bulkDisabledReason(this.bulkAtp.eligible, this.atpDisabledReason);
  }

  declineAllActive: boolean = false;
  declineAllSites: SiteDetail[] = [];
  declineAllItems: BatchDeclineItem[] = [];
  declineScopeLabel = 'on this project';

  onSiteBulkAction(site: SiteVm, event: { action: SiteBulkAction; rows: SiteDetail[] }): void {
    if (this.readOnly || this.rowsLocked(event.rows)) {
      return;
    }
    switch (event.action) {
      case 'submit': this.openSubmitAllQuotes(event.rows); break;
      case 'accept': this.openAcceptAllOffers(event.rows, siteScopeLabel(site.siteNumber)); break;
      case 'atp': this.generateATP(event.rows); break;
      case 'decline': this.openDeclineAll(event.rows, siteScopeLabel(site.siteNumber)); break;
    }
  }

  openDeclineAll(rows: SiteDetail[] = this.bulkDecline.eligible, scopeLabel = 'on this project'): void {
    if (this.readOnly || this.rowsLocked(rows) || !rows.length || tooManyToDecline(rows)) {
      return;
    }
    this.declineAllSites = [...rows];
    this.declineAllItems = rows.map((row) => batchDeclineItem(row));
    this.declineScopeLabel = scopeLabel;
    this.declineAllActive = true;
  }

  closeDeclineAllModal(): void {
    this.declineAllActive = false;
  }

  declineAll(fields: Omit<BatchDeclineRequest, 'proposalIds'>): void {
    if (
      this.readOnly || this.rowsLocked(this.declineAllSites) || !this.declineAllSites.length ||
      tooManyToDecline(this.declineAllSites)
    ) {
      return;
    }
    this.lock.hold(this.proposalIds(this.declineAllSites), this.batchDeclineService.declineAll(this.project.raw, this.declineAllSites, fields));
  }

  // ATP artefacts (generated on demand before signing) + the confirmation copy,
  // same fields `product-line` carries for the single-product sign.
  atpDocument: any = null;
  atpDocumentPath: SafeResourceUrl = '';
  childModalHeaderTitle: string = 'ATP Signed & Project Active';
  childModalBody: string =
    'You have signed the ATP and the project can be viewed from the Active tab.';

  // ---------------------------------------------------------------------------
  // Submit All Quotes → the existing allbids-dialog (requirements
  // acknowledgement + itemized confirmation), which owns the ProposalService
  // write and the list refresh. `[data]` gets the whole project and
  // `[siteDetails]` the eligible set — see the template for why that split
  // is load-bearing.
  // ---------------------------------------------------------------------------
  openSubmitAllQuotes(rows: SiteDetail[] = this.bulkSubmit.eligible): void {
    if (this.readOnly || this.rowsLocked(rows) || !rows.length) {
      return;
    }
    this.bulkQuoteSites = [...rows];
    this.allBidsActive = true;
  }

  toggleSubmitAllQuotesModal(): void {
    this.allBidsActive = !this.allBidsActive;
  }

  // ---------------------------------------------------------------------------
  // Accept All Offers → the existing accept-offered-proposal-dialog in its
  // `[multiple]="true"` mode, which owns the write, the refresh and (via
  // ModalService) the plural page-hosted confirmation.
  // ---------------------------------------------------------------------------
  openAcceptAllOffers(rows: SiteDetail[] = this.bulkAccept.eligible, scopeLabel = ''): void {
    if (this.readOnly || this.rowsLocked(rows) || !rows.length) {
      return;
    }
    this.bulkOfferSites = [...rows];
    this.acceptScopeLabel = scopeLabel;
    this.acceptAllOffersActive = true;
  }

  toggleAcceptAllOffersModal(): void {
    this.acceptAllOffersActive = !this.acceptAllOffersActive;
  }

  // ---------------------------------------------------------------------------
  // Sign All ATPs. Reuses the existing relay exactly:
  // `generateAtp` on a payload carrying every eligible BID_WON product
  // returns ONE document covering all of them, then the existing
  // signatp-dialog collects the signature.
  // ---------------------------------------------------------------------------
  generateATP(rows: SiteDetail[] = this.bulkAtp.eligible): void {
    if (this.readOnly || this.rowsLocked(rows) || !rows.length || this.atpInProgress) {
      return;
    }
    // One ATP at a time per project: this banner hosts one sign dialog and
    // keeps one set of ATP fields, and two sites' products never share a
    // lock, so `atpInProgress` is what stops a second Sign All ATPs from
    // overwriting the first one's document or products.
    const atpSites = [...rows];
    this.atpGenerating = true;
    this.toastr.info(
      'Please wait a moment while we generate your ATP document. This process may take a few seconds.'
    );
    this.lock
      .hold(this.proposalIds(atpSites), this.siteService.generateAtp(this.bulkProjectDetail(atpSites), this.user.id ?? ''))
      .subscribe({ next: (response) => {
        this.atpGenerating = false;
        if (response && response.status === 'SUCCESS' && response.data) {
          if (response.data.documentPath) {
            // The document and the products it covers change together.
            this.bulkAtpSites = atpSites;
            this.atpDocument = response.data;
            this.atpDocumentPath = this.sanitizer.bypassSecurityTrustResourceUrl(
              this.atpDocument.documentPath
            );
            this.signAtpActive = true;
          } else {
            this.toastr.error(
              'There was an issue with generating your ATP document. Please try again.'
            );
          }
        } else {
          this.toastr.error(
            (response && response.message) ||
              'There was an issue with generating your ATP document. Please try again.'
          );
        }
      }, error: () => {
        this.atpGenerating = false;
        this.toastr.error('An error occurred. Please try again.');
      }, complete: () => {
        // An interceptor can complete without a response (read-only alias).
        this.atpGenerating = false;
      } });
  }

  toggleATPModal(): void {
    this.signAtpActive = !this.signAtpActive;
  }

  signATP(event: { fullName: string }): void {
    if (this.readOnly || this.rowsLocked(this.bulkAtpSites)) {
      return;
    }
    this.toastr.info('Accepting your signed ATP...');
    this.lock
      .hold(this.proposalIds(this.bulkAtpSites), this.siteService.signAtp(
        {
          projectDetail: this.bulkProjectDetail(this.bulkAtpSites),
          atpDocument: this.atpDocument,
          atpSignedBy: event.fullName,
        },
        this.user.id ?? ''
      ))
      .subscribe({ next: (response) => {
        // Emit BEFORE the project-detail push below, for the same reason
        // `product-line.signATP` does: that refresh drops the signed BID_WON
        // products off this screen and destroys this banner, which tears down
        // the (atpSigned) listener chain the visits travel on. Emitted on
        // every signature, the empty list included, so a second sign cannot
        // inherit the first one's visits.
        this.atpSigned.emit(response.data?.siteVisits ?? []);
        const updatedProjectDetail = response.data?.projectDetail;
        if (updatedProjectDetail) {
          this.projectService.updateProjectDataSubjectWithProjectDetail(
            updatedProjectDetail
          );
        }
        this.signAtpActive = false;
        // Page-hosted confirmation — every page that mounts this banner
        // already hosts `atp-sign-accepted-dialog` bound to this flag, so no
        // new dialog instance is needed anywhere.
        this.modalService.toggleOpenAtpSignAcceptedModal();
      }, error: () => {
        this.signAtpActive = false;
        this.toastr.error('An error occurred. Please try again.');
      } });
  }

  declineATP(event: {
    declineReason: string;
    declineOtherReason: string;
    declineComments: string;
  }): void {
    if (this.readOnly || this.rowsLocked(this.bulkAtpSites)) {
      return;
    }
    // Decline reasons are stamped onto the AWARDED set only — never onto
    // `project.raw.sites`, which on Project Overview also carries this
    // project's submitted/active products.
    const declinedSites = this.bulkAtpSites.map((siteDetail: any) => ({
      ...siteDetail,
      proposal: {
        ...siteDetail.proposal,
        declineReason: event.declineReason,
        declineOtherReason: event.declineOtherReason,
        declineComments: event.declineComments,
      },
    }));
    this.lock
      .hold(this.proposalIds(declinedSites), this.siteService.cancel(this.user.id ?? '', this.bulkProjectDetail(declinedSites)))
      .subscribe({ next: (response: any) => {
        if (response.status === 'FAILURE') {
          this.toastr.error(
            response.message || 'An error occurred. Please try again.'
          );
          return;
        }
        if (response.data) {
          this.signAtpActive = false;
          // Page-hosted decline confirmation (isAtp copy) — opened BEFORE the
          // refresh that destroys this banner, as `product-line.decline` does.
          this.modalService.toggleOpenDeclineSuccessModal({}, false, true);
          this.projectService.updateProjectDataSubjectWithProjectDetail(
            response.data
          );
        } else {
          this.toastr.error('An error occurred. Please try again.');
        }
      }, error: () => {
        this.toastr.error('An error occurred. Please try again.');
      } });
  }

  /**
   * The ProjectDetail a bulk write posts: the page's project with only the
   * products being swept. `buildBulkProjectDetail` is `product-line`'s
   * `buildProjectDetail` widened from one site to N.
   */
  private bulkProjectDetail(siteDetails: SiteDetail[]): any {
    return buildBulkProjectDetail(this.project.raw, siteDetails);
  }

  /** The products a bulk write touches: the keys it holds the lock on. */
  private proposalIds(rows: SiteDetail[]): (string | undefined)[] {
    return rows.map((row) => row.proposal?.id);
  }
}
